import { formatGermanNumber } from './parseGermanNumber';

export type MetalQuoteSource = 'manual' | 'westmetall' | 'sonepar';

export type MetalQuoteSuggestion = {
  copperQuoteEurPer100kg: number;
  quoteDate: string | null;
  note: string | null;
};

export const METAL_QUOTE_SOURCE_LABELS: Record<MetalQuoteSource, string> = {
  westmetall: 'Westmetall WM-Notiz',
  sonepar: 'Sonepar SME / SAN',
  manual: 'manuelle Notierung',
};

export function copperQuoteFieldLabel(source: MetalQuoteSource): string {
  if (source === 'sonepar') return 'Kupfernotierung SME';
  if (source === 'westmetall') return 'Obere Kupfer-WM-Notiz';
  return 'Kupfernotierung';
}

export function aluminumQuoteFieldLabel(source: MetalQuoteSource): string {
  return source === 'sonepar' ? 'Aluminiumnotierung SAN' : 'Aluminiumnotierung';
}

/**
 * Holt die aktuelle obere Kupfer-WM-Notiz. Aluminium liefert Westmetall nicht,
 * das bleibt eine manuelle Eingabe.
 */
export type PositionMetalKind = 'cu' | 'al';

/** Zeigt den Zuschlag nur, wenn der EK noch der zuletzt gerechnete Wert ist. */
export function visibleMetalSurchargeHint(row: {
  unit_cost_ek: number;
  metal_kind?: PositionMetalKind | null;
  metal_surcharge?: number | null;
  metal_priced_ek?: number | null;
}): { kind: PositionMetalKind; amount: number } | null {
  if (row.metal_kind !== 'cu' && row.metal_kind !== 'al') return null;
  if (row.metal_surcharge == null || !Number.isFinite(row.metal_surcharge) || row.metal_surcharge <= 0) {
    return null;
  }
  if (row.metal_priced_ek == null || !Number.isFinite(row.metal_priced_ek)) return null;
  if (Math.abs(row.unit_cost_ek - row.metal_priced_ek) >= 0.005) return null;
  return { kind: row.metal_kind, amount: row.metal_surcharge };
}

export function formatMetalSurchargeHint(kind: PositionMetalKind, amount: number): string {
  const label = kind === 'cu' ? 'CU Zuschlag' : 'Alu Zuschlag';
  return `${label}: ${formatGermanNumber(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
}

export async function fetchCurrentWestmetallQuote(): Promise<MetalQuoteSuggestion> {
  const res = await fetch('/api/metal-quotes/current');
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || 'Aktuelle WM-Notiz konnte nicht geladen werden.');
  }
  const suggestion = data?.suggestion as
    | { copperQuoteEurPer100kg?: number; quoteDate?: string; note?: string }
    | undefined;
  if (!suggestion || !Number.isFinite(suggestion.copperQuoteEurPer100kg)) {
    throw new Error('Aktuelle WM-Notiz ist unvollständig.');
  }
  return {
    copperQuoteEurPer100kg: Number(suggestion.copperQuoteEurPer100kg),
    quoteDate: suggestion.quoteDate ?? null,
    note: suggestion.note ?? null,
  };
}
